import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { fetchProductsBySlug, productBySlugKey } from '@/lib/hook/useProduct'

/**
 * Enriches light products (recommend, recent) with real pricing data from CS-Cart backend.
 *
 * The search index stores a single "default" price per product, which doesn't reflect
 * user-group-specific pricing, tiered pricing (prices_current), or active promotions.
 * This hook fetches the full product data via getProductBySlug (REST seo->products)
 * so that ModernProduct / getProductPrice() can display accurate prices and promotions.
 */
const useEnrichProducts = (products: any[], enabled = true) => {
  const router = useRouter()
  const locale = (router.query.locale as string) || 'fr'

  const slugs = products.map(p => p.seo_name).filter(Boolean)

  const { data, isLoading: loading } = useQuery({
    queryKey: productBySlugKey(slugs, null),
    queryFn: () => fetchProductsBySlug(slugs, null, locale),
    enabled: enabled && slugs.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const enrichedProducts = useMemo(() => {
    if (!data || slugs.length === 0) {
      return products
    }

    const enrichedMap = new Map<string, any>()
    for (const enriched of data) {
      if (enriched?.seo_name) {
        enrichedMap.set(enriched.seo_name, enriched)
      }
    }

    return products.map(product => {
      const enriched = enrichedMap.get(product.seo_name)
      if (!enriched) return product

      return {
        ...product,
        ...enriched,
        // Preserve index image data as fallback
        main_pair: enriched.main_pair || product.main_pair,
        image_pairs: enriched.image_pairs?.length ? enriched.image_pairs : product.image_pairs,
      }
    })
  }, [data, products])

  const enriched = !!data && !loading && slugs.length > 0

  return { products: enrichedProducts, loading, enriched }
}

export default useEnrichProducts
